import { Injectable, inject, signal, effect } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { environment } from '../../../environments/environment';
import { AppStore } from '../../stores/app.store';
import { AuthStore } from '../../stores/auth.store';
import { SignalRMethods } from './signalr.constants';

const RESTART_DELAYS_MS = [2000, 5000, 10000, 30000, 60000];

@Injectable({ providedIn: 'root' })
export class SignalRService {
  private readonly appStore = inject(AppStore);
  private readonly authStore = inject(AuthStore);

  private connection: signalR.HubConnection | null = null;
  private currentGroupId: Guid | null = null;
  private currentUserGroupId: Guid | null = null;
  private restartAttempt = 0;
  private readonly pendingHandlers: Array<{ event: string; callback: (...args: any[]) => void }> = [];

  private readonly _isConnected = signal(false);

  readonly isConnected = this._isConnected.asReadonly();

  // Auto-rejoin facility group when facility changes, regardless of which page is active
  private readonly rejoinOnFacilityChange = effect(() => {
    const facilityId = this.appStore.selectedFacilityId();
    if (facilityId && this._isConnected()) {
      void this.joinFacilityGroup();
    }
  });

  private readonly rejoinOnUserChange = effect(() => {
    this.authStore.user();
    if (this._isConnected()) {
      void this.joinUserGroup();
    }
  });

  async start(): Promise<void> {
    if (this.connection) {
      return;
    }

    // Build connection BEFORE start() so on() handlers can register on it
    this.connection = this.createConnection();

    // Replay any handlers that were registered before start()
    for (const { event, callback } of this.pendingHandlers) {
      this.connection.on(event, callback);
    }
    this.pendingHandlers.length = 0;

    this.connection.onreconnected(async () => {
      this._isConnected.set(true);
      this.currentGroupId = null;
      await this.joinFacilityGroup();
      this.currentUserGroupId = null;
      await this.joinUserGroup();
    });

    this.connection.onreconnecting(() => {
      this._isConnected.set(false);
    });

    this.connection.onclose(() => {
      this._isConnected.set(false);
      this.scheduleRestart();
    });

    await this.connect();
  }

  private async connect(): Promise<void> {
    if (!this.connection) {
      return;
    }

    try {
      await this.connection.start();
    } catch (err) {
      console.error('SignalR connection failed:', err);
      this.scheduleRestart();

      return;
    }

    this.restartAttempt = 0;
    this.currentGroupId = null;
    this.currentUserGroupId = null;
    this._isConnected.set(true);

    try {
      await this.joinFacilityGroup();
      await this.joinUserGroup();
    } catch (err) {
      console.error('SignalR group join failed:', err);
    }
  }

  private scheduleRestart(): void {
    const delayMs = RESTART_DELAYS_MS[Math.min(this.restartAttempt, RESTART_DELAYS_MS.length - 1)];

    this.restartAttempt++;
    setTimeout(() => void this.connect(), delayMs);
  }

  protected createConnection(): signalR.HubConnection {
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(environment.signalRHubUrl, {
        transport: signalR.HttpTransportType.WebSockets,
        skipNegotiation: true,
        accessTokenFactory: () => this.authStore.token() ?? '',
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 20000, 30000, 60000])
      .withStatefulReconnect()
      .withKeepAliveInterval(30000)
      .withServerTimeout(60000)
      .build();
    return connection;
  }

  async joinFacilityGroup(): Promise<void> {
    const facilityId = this.appStore.selectedFacilityId();
    if (!this.connection || !facilityId) {
      return;
    }

    // Leave previous group if different
    if (this.currentGroupId && this.currentGroupId !== facilityId) {
      await this.connection.invoke(SignalRMethods.LeaveDataNotificationGroup, this.currentGroupId);
      this.currentGroupId = null;
    }

    if (this.currentGroupId !== facilityId) {
      await this.connection.invoke(SignalRMethods.JoinDataNotificationGroup, facilityId);
      this.currentGroupId = facilityId;
    }
  }

  async joinUserGroup(): Promise<void> {
    const userId = this.authStore.user()?.id ?? null;
    if (!this.connection) {
      return;
    }

    if (this.currentUserGroupId && this.currentUserGroupId !== userId) {
      await this.connection.invoke(SignalRMethods.LeaveDataNotificationGroup, this.currentUserGroupId);
      this.currentUserGroupId = null;
    }

    if (userId && this.currentUserGroupId !== userId) {
      await this.connection.invoke(SignalRMethods.JoinDataNotificationGroup, userId);
      this.currentUserGroupId = userId;
    }
  }

  on<T>(event: string, callback: (data: T) => void): void {
    if (this.connection) {
      this.connection.on(event, callback);
    } else {
      // Queue for replay when connection is built
      this.pendingHandlers.push({ event, callback });
    }
  }

  off(event: string): void {
    this.connection?.off(event);
  }
}
